import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface ParcelLocation {
  id: string;
  courier_code: string;
  identifier: string;
  type: string;
  name: string;
  address: string;
  city: string;
  postal_code: string;
  country_code: string;
  comment: string | null;
  latitude: string;
  longitude: string;
}

const COURIER_LABELS: Record<string, string> = {
  lp_express: "LP Express",
  omniva_lt: "Omniva",
  venipak: "Venipak",
  dpd: "DPD",
};

export const getCourierLabel = (code: string) => COURIER_LABELS[code] || code;

export const useParcelLocations = (country = "LT", enabled = true) => {
  const [locations, setLocations] = useState<ParcelLocation[]>([]);
  const [grouped, setGrouped] = useState<Record<string, ParcelLocation[]>>({});
  const [couriers, setCouriers] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;

    const fetchLocations = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // Use direct fetch with query string — supabase.functions.invoke doesn't pass query params reliably
        const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
        const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
        const res = await fetch(
          `https://${projectId}.supabase.co/functions/v1/get-parcel-locations?country=${country}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              apikey: anonKey,
              Authorization: `Bearer ${anonKey}`,
            },
          }
        );

        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const result = await res.json();

        setLocations(result?.locations || []);
        setGrouped(result?.grouped || {});
        setCouriers(result?.couriers || []);
        console.log(`[useParcelLocations] Loaded ${result?.locations?.length || 0} locations`);
      } catch (e: any) {
        console.error("Failed to fetch parcel locations:", e);
        setError(e.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLocations();
  }, [country, enabled]);

  return { locations, grouped, couriers, isLoading, error };
};
